import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import {
  Button,
  Header,
  IconWithCustomColor,
  NegativeIcon,
  Paragraph,
  PositiveIcon,
  RichTextDisplay,
  swiperLogo,
} from "@datagame/ui";
import { useEffect, useMemo, useState } from "react";

import { AnswerResultProvider } from "../../contexts/answerResult";
import { Attribute, SwiperConfiguration } from "../../gql/graphql";
import {
  CARDS_TO_RENDER,
  COLOR_CORRECT_GREEN,
  COLOR_CORRECT_GREEN_LIGHT,
  COLOR_NEUTRALS_WHITE,
  COLOR_WRONG_RED,
  COLOR_WRONG_RED_LIGHT,
} from "../../constants";
import { ProgressBar } from "../shared/ProgressBar";
import { SwiperCard, SwiperCardPlaceholder } from "./Card";
import { useTranslation } from "react-i18next";
import { useGlobalConfiguration } from "../../hooks/useGlobalConfiugration";
import { useGameVisuals } from "../../contexts/gameVisuals";
import { shuffleArray } from "../../utils";

import styles from "./Swiper.module.scss";

export interface AttributeAnswer {
  attributeId: string;
  result: boolean;
}
interface SwiperProps {
  onFinish: (answers: AttributeAnswer[]) => unknown;
  configuration: SwiperConfiguration;
}

export const Swiper = ({ onFinish, configuration }: SwiperProps) => {
  const {
    attributes,
    correctSwipeText,
    incorrectSwipeText,
    showSwipeCorrectness,
    question,
    showButtons,
    likeText,
    shuffleCards,
    unlikeText,
  } = configuration;
  const shuffledAttributes = useMemo(
    () => (shuffleCards ? shuffleArray(attributes) : attributes),
    [attributes, shuffleCards],
  );
  const { t } = useTranslation("common");
  const { hideDatagameBranding } = useGlobalConfiguration();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<AttributeAnswer[]>([]);
  const isLastAnswerCorrect = useMemo(() => {
    const lastAnswer = answers[answers.length - 1];
    if (!lastAnswer) {
      return undefined;
    }
    return (
      shuffledAttributes.find((attribute) => attribute.id === lastAnswer.attributeId)?.isCorrect === lastAnswer.result
    );
  }, [answers.length]);

  const wrapperControls = useAnimationControls();
  const tipControls = useAnimationControls();
  const gameVisuals = useGameVisuals();
  const { positiveColor, negativeColor, showIcons, themeColor, iconSet } = gameVisuals || {};
  const currentQuestion = shuffledAttributes[currentQuestionIndex];
  const isFinished = currentQuestionIndex === shuffledAttributes.length;
  const attributesToRender = shuffledAttributes
    .slice(currentQuestionIndex, Math.min(shuffledAttributes.length, currentQuestionIndex + CARDS_TO_RENDER))
    .reverse();

  const renderCard = (attribute: Attribute, index: number) => {
    if (index === attributesToRender.length - 1) {
      return (
        <SwiperCard key={attribute.id} attribute={attribute} onSwipe={answerCurrentQuestion} showIcons={showIcons} />
      );
    }
    return <SwiperCardPlaceholder key={index} attribute={attribute} />;
  };

  const answerCurrentQuestion = (result: boolean) => {
    setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    setAnswers((prevAnswers) => [
      ...prevAnswers,
      {
        attributeId: currentQuestion.id,
        result,
      },
    ]);
  };

  useEffect(() => {
    if (isFinished) {
      setTimeout(() => onFinish(answers), 800);
    }
  }, [isFinished]);

  useEffect(() => {
    if (isLastAnswerCorrect === undefined || !showSwipeCorrectness) {
      return;
    }

    const sharedAnimation = {
      backgroundColor: [
        COLOR_NEUTRALS_WHITE,
        isLastAnswerCorrect ? COLOR_CORRECT_GREEN_LIGHT : COLOR_WRONG_RED_LIGHT,
        COLOR_NEUTRALS_WHITE,
      ],
      transition: {
        duration: 1,
      },
    };

    wrapperControls.start(sharedAnimation);
    tipControls.start({
      ...sharedAnimation,
      opacity: [0, 1, 0],
    });
  }, [answers.length]);

  return (
    <motion.div animate={wrapperControls} className={styles.wrapper} id="swiper-white-background">
      <ProgressBar
        className={styles.progressBar}
        color={themeColor}
        steps={attributes.length}
        currentStep={currentQuestionIndex}
      />
      {!hideDatagameBranding && (
        <>
          <img src={swiperLogo} alt="swiper" className={styles.image} />

          <Header className={styles.headline} variant="h5">
            {t("swiper.headline")}
          </Header>
        </>
      )}
      <Paragraph size="small" weight="font-bold" className={styles.tip}>
        <RichTextDisplay value={question} />
      </Paragraph>
      <div className={styles.swiperControls}>
        <div className={styles.cardsStack} id="card-stack">
          <AnswerResultProvider answers={answers}>
            <AnimatePresence>{attributesToRender.map(renderCard)}</AnimatePresence>
          </AnswerResultProvider>
        </div>

        <motion.div className={styles.resultWrapper} animate={tipControls} style={{ opacity: 0 }}>
          <Header
            variant="h5"
            style={{
              color: isLastAnswerCorrect ? COLOR_CORRECT_GREEN : COLOR_WRONG_RED,
            }}
            className={styles.resultText}
          >
            {isLastAnswerCorrect ? correctSwipeText : incorrectSwipeText}
          </Header>
          <IconWithCustomColor
            className={styles.resultIcon}
            icon={isLastAnswerCorrect ? <PositiveIcon iconSetKey={iconSet} /> : <NegativeIcon iconSetKey={iconSet} />}
            innerFill={isLastAnswerCorrect ? COLOR_CORRECT_GREEN : COLOR_WRONG_RED}
          />
        </motion.div>

        {showButtons && (
          <div className={styles.buttonContainer}>
            <Button
              className={styles.button}
              size="large"
              iconSide="left"
              kind="outline"
              color="purple"
              customColor={negativeColor}
              icon={showIcons ? <NegativeIcon iconSetKey={iconSet} /> : undefined}
              disabled={isFinished}
              onClick={() => answerCurrentQuestion(false)}
            >
              {unlikeText}
            </Button>
            <Button
              onClick={() => answerCurrentQuestion(true)}
              className={styles.button}
              size="large"
              iconSide="left"
              disabled={isFinished}
              style={{ background: positiveColor }}
              icon={showIcons ? <PositiveIcon iconSetKey={iconSet} /> : undefined}
            >
              {likeText}
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};
